import { AfterViewInit, Directive, Input, Optional, Self } from "@angular/core";
import { DropdownComponent, DropdownMultiselectWithChipsComponent } from "@flowtribe/ui";

/**
 * Tags the host dropdown's CDK overlay pane with the given class, so a rule can target
 * that one menu's contents instead of every ui-dropdown in the app.
 *
 * mat-menu renders its panel in an overlay appended to <body>, so the panel is not a
 * descendant of the page that declares the dropdown: neither a descendant selector nor
 * the component's own styles can reach it. `MatMenu.overlayPanelClass` is read once, when
 * the trigger builds its overlay on first open, so the class is appended here in
 * ngAfterViewInit — by then the inner `ui-dropdown` has resolved its own MatMenuTrigger.
 *
 * Usage: <ui-dropdown dropdownMenuClass="my-menu"> or the same on
 * <ui-dropdown-multiselect-with-chips>, which owns a `ui-dropdown` internally.
 */
@Directive({
  standalone: false,
  selector: "ui-dropdown[dropdownMenuClass], ui-dropdown-multiselect-with-chips[dropdownMenuClass]"
})
export class DropdownMenuClassDirective implements AfterViewInit {
  @Input() dropdownMenuClass = "";

  constructor(
    // @Self: without it, DI would walk up to an enclosing ui-dropdown when the host is
    // the multiselect, whose own element provides no DropdownComponent.
    @Optional() @Self() private readonly dropdown: DropdownComponent<unknown> | null,
    @Optional() @Self() private readonly multiselect: DropdownMultiselectWithChipsComponent<unknown> | null
  ) {}

  ngAfterViewInit(): void {
    const menu = (this.dropdown ?? this.multiselect?.dropdown)?.menuTrigger?.menu;
    if (!menu || !this.dropdownMenuClass) {
      return;
    }

    // Append rather than assign: the class is additive to whatever the library set.
    const existing = menu.overlayPanelClass;
    const classes = Array.isArray(existing) ? existing : existing ? [existing] : [];
    menu.overlayPanelClass = [...classes, this.dropdownMenuClass];
  }
}
